import { Dispatch } from "react";
import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import moment from "moment/moment";
import type { SelectionAmil, SelectionKariah } from "./api";
import { fetchSelectionAmil, fetchSelectionKariah } from "./api";

export interface ReportForm {
  search: string;
  date: string;
  start_date: string;
  end_date: string;
  agency: string;
  amil_choice: string | null;
  amil_choices: SelectionAmil[];
  kariah_choice: string | null;
  kariah_choices: SelectionKariah[];
  loading: boolean;
}

const today = moment();
const tomorrow = moment().add(1, "day");

const defaultStart = today.format("YYYY/MM/DD");
const defaultEnd = tomorrow.format("YYYY/MM/DD");

const initialState: ReportForm = {
  search: "",
  date: defaultStart,
  start_date: defaultStart,
  end_date: defaultEnd,
  agency: "",
  amil_choice: null,
  amil_choices: [],
  kariah_choice: null,
  kariah_choices: [],
  loading: false,
};

const formStateSlice = createSlice({
  name: "formState",
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setSearch(state, action: PayloadAction<string>) {
      state.search = action.payload;
    },
    setDate(state, action: PayloadAction<string>) {
      state.date = action.payload;
    },
    setDateRange(
      state,
      action: PayloadAction<{ start_date: string; end_date: string }>
    ) {
      const { start_date, end_date } = action.payload;
      state.start_date = start_date;
      state.end_date = end_date;
    },
    setStartDate(state, action: PayloadAction<string>) {
      state.start_date = action.payload;
    },
    setEndDate(state, action: PayloadAction<string>) {
      state.end_date = action.payload;
    },
    setAmilChoice(state, action: PayloadAction<string | null>) {
      state.amil_choice = action.payload;
    },
    setAmilChoices(state, action: PayloadAction<SelectionAmil[]>) {
      state.amil_choices = action.payload;
    },
    setKariahChoice(state, action: PayloadAction<string | null>) {
      state.kariah_choice = action.payload;
    },
    setKariahChoices(state, action: PayloadAction<SelectionKariah[]>) {
      state.kariah_choices = action.payload;
    },
    setAgency(state, action: PayloadAction<string>) {
      state.agency = action.payload;
    },
  },
});

export const {
  setLoading,
  setSearch,
  setDate,
  setStartDate,
  setEndDate,
  setAmilChoice,
  setAmilChoices,
  setKariahChoice,
  setKariahChoices,
  setAgency,
  setDateRange,
} = formStateSlice.actions;

export const updateAmilChoices = (
  token: string,
  data?: { [key: string]: string }
) => async (dispatch: Dispatch<PayloadAction<SelectionAmil[]>>) => {
  try {
    let extras: { [key: string]: string } | undefined = undefined;

    if (data) {
      const { agency } = data;
      extras = {
        agency,
      };
    }

    const list = await fetchSelectionAmil(extras)(token);
    dispatch(setAmilChoices(list));
  } catch (e) {
    console.log(e);
    throw e;
  }
};

export const updateKariahChoices = (token: string, agency: string) => async (
  dispatch: Dispatch<PayloadAction<SelectionKariah[]>>
) => {
  try {
    if (!agency) {
      dispatch(setKariahChoices([]));
      return;
    }

    let extras: { [key: string]: string } | undefined = undefined;

    if (agency) {
      extras = {
        agency,
      };
    }

    const list = await fetchSelectionKariah(extras)(token);
    dispatch(setKariahChoices(list));
  } catch (e) {
    console.log(e);
    throw e;
  }
};

export default formStateSlice.reducer;
